/* Pixel Threads — RESOLVE v1.0
   Attention is the rendering engine.
   Marked imagery loads as a coarse 1-bit ordered dither and resolves as
   the visitor gives it attention. Empty chapters carry a field that never
   resolves. Progressive enhancement only: the real <img> is never touched,
   and every failure path removes the overlay and leaves the page as shipped.
   Targets are configured in TARGETS. No Liquid dependencies beyond the
   loader tag in layout/theme.liquid. */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;
  if (!('IntersectionObserver' in window) || !window.requestAnimationFrame) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var MOBILE = window.matchMedia('(max-width: 779px)').matches;
  var rootCS = window.getComputedStyle(document.documentElement);
  var INK = (rootCS.getPropertyValue('--ink') || '').trim() || '#080808';
  var PAPER = (rootCS.getPropertyValue('--paper') || '').trim() || '#F1F0E9';

  var BAYER = [
    0, 32, 8, 40, 2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44, 4, 36, 14, 46, 6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
    3, 35, 11, 43, 1, 33, 9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47, 7, 39, 13, 45, 5, 37,
    63, 31, 55, 23, 61, 29, 53, 21
  ];

  /* mode: scroll = resolves with scroll attention, holds when you stop.
     fast = resolves on its own shortly after entering the viewport.
     never = a faint field that never resolves.
     mobile = whether the target runs below 780px. */
  var TARGETS = [
    { sel: '.hero-art-window', mode: 'scroll', mobile: true },
    { sel: '.article-image', mode: 'scroll', mobile: true },
    { sel: '.product-image', mode: 'fast', mobile: false },
    { sel: '.chapter-pending', mode: 'never', mobile: true }
  ];

  var seen = false;
  try { seen = sessionStorage.getItem('pt-resolved') === '1'; } catch (e) {}

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  function effectiveBgIsDark(el) {
    var node = el;
    while (node && node !== document.documentElement) {
      var bg = window.getComputedStyle(node).backgroundColor;
      if (bg && bg !== 'transparent' && bg.indexOf('rgba(0, 0, 0, 0)') !== 0) {
        var m = bg.match(/\d+/g);
        if (m && m.length >= 3) {
          return (Number(m[0]) * 299 + Number(m[1]) * 587 + Number(m[2]) * 114) / 1000 < 128;
        }
      }
      node = node.parentElement;
    }
    return false;
  }

  var instances = [];
  var rafId = null;
  var lastT = 0;
  var scrollDelta = 0;
  var lastY = window.pageYOffset;

  window.addEventListener('scroll', function () {
    var y = window.pageYOffset;
    scrollDelta += Math.abs(y - lastY);
    lastY = y;
  }, { passive: true });

  function Instance(el, mode) {
    this.el = el;
    this.mode = mode;
    this.img = null;
    this.canvas = null;
    this.ctx = null;
    this.lum = null;
    this.sw = 0;
    this.sh = 0;
    this.r = (seen && mode === 'scroll') ? 0.6 : 0;
    this.lastDrawn = -1;
    this.fieldClock = 99;
    this.fieldDrawn = false;
    this.fieldInk = INK;
    this.visible = false;
    this.ready = false;
    this.done = false;
  }

  Instance.prototype.mount = function () {
    var el = this.el;
    if (window.getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }
    var c = document.createElement('canvas');
    c.setAttribute('aria-hidden', 'true');
    c.className = 'pt-resolve-canvas';
    c.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;display:block;z-index:2;';
    if (this.mode === 'never') {
      c.style.zIndex = '0';
      c.style.width = '100%';
      c.style.height = '100%';
      this.fieldInk = effectiveBgIsDark(el) ? PAPER : INK;
      c.style.opacity = this.fieldInk === PAPER ? '0.16' : '0.12';
      for (var i = 0; i < el.children.length; i++) {
        var ch = el.children[i];
        var cs = window.getComputedStyle(ch);
        if (cs.position === 'static') ch.style.position = 'relative';
        if (cs.zIndex === 'auto') ch.style.zIndex = '1';
      }
      this.ready = true;
    }
    el.appendChild(c);
    this.canvas = c;
    this.ctx = c.getContext('2d');
    if (!this.ctx) this.destroy();
  };

  Instance.prototype.loadSource = function () {
    var self = this;
    var img = this.el.querySelector('img');
    if (!img) { this.destroy(); return; }
    this.img = img;
    function begin() {
      var src = img.currentSrc || img.src;
      if (!src) { self.destroy(); return; }
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () {
        try {
          var s = document.createElement('canvas');
          var down = 180;
          var ratio = probe.naturalWidth ? probe.naturalHeight / probe.naturalWidth : 1;
          s.width = down;
          s.height = Math.max(2, Math.round(down * ratio));
          var sc = s.getContext('2d', { willReadFrequently: true });
          sc.drawImage(probe, 0, 0, s.width, s.height);
          self.lum = sc.getImageData(0, 0, s.width, s.height).data;
          self.sw = s.width;
          self.sh = s.height;
          self.ready = true;
          self.lastDrawn = -1;
          ensureLoop();
        } catch (e) {
          self.destroy();
        }
      };
      probe.onerror = function () { self.destroy(); };
      probe.src = src;
    }
    if (img.complete && img.naturalWidth) { begin(); }
    else { img.addEventListener('load', begin, { once: true }); }
  };

  Instance.prototype.place = function () {
    if (!this.img) return { w: this.el.clientWidth, h: this.el.clientHeight };
    var er = this.el.getBoundingClientRect();
    var ir = this.img.getBoundingClientRect();
    var st = this.canvas.style;
    st.left = (ir.left - er.left) + 'px';
    st.top = (ir.top - er.top) + 'px';
    st.width = ir.width + 'px';
    st.height = ir.height + 'px';
    return { w: ir.width, h: ir.height };
  };

  Instance.prototype.draw = function () {
    if (this.done || !this.ready) return;
    if (this.mode === 'never') { this.drawField(); return; }
    var r = this.r;
    if (Math.abs(r - this.lastDrawn) < 0.006) return;
    this.lastDrawn = r;
    var box = this.place();
    var w = Math.round(box.w * 0.5);
    var h = Math.round(box.h * 0.5);
    if (w < 4 || h < 4) return;
    var c = this.canvas;
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    var ctx = this.ctx;
    var t = clamp01(r / 0.85);
    var cell = Math.max(1, Math.round(8 - t * 7));
    var lum = this.lum;
    var sw = this.sw;
    var sh = this.sh;
    var srcAR = sw / sh;
    var dstAR = w / h;
    var cw, chh, cx, cy;
    if (srcAR > dstAR) { chh = sh; cw = sh * dstAR; cx = (sw - cw) / 2; cy = 0; }
    else { cw = sw; chh = sw / dstAR; cx = 0; cy = (sh - chh) / 2; }
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = INK;
    var gh = Math.ceil(h / cell);
    var gw = Math.ceil(w / cell);
    for (var gy = 0; gy < gh; gy++) {
      var sy = Math.min(sh - 1, Math.max(0, Math.floor(cy + ((gy * cell + cell * 0.5) / h) * chh)));
      var rowOff = sy * sw;
      var bRow = (gy & 7) << 3;
      for (var gx = 0; gx < gw; gx++) {
        var sx = Math.min(sw - 1, Math.max(0, Math.floor(cx + ((gx * cell + cell * 0.5) / w) * cw)));
        var o = (rowOff + sx) << 2;
        var L = (lum[o] * 299 + lum[o + 1] * 587 + lum[o + 2] * 114) / 255000;
        if (L < (BAYER[bRow | (gx & 7)] + 0.5) / 64) {
          ctx.fillRect(gx * cell, gy * cell, cell, cell);
        }
      }
    }
    var op = r <= 0.85 ? 1 : 1 - (r - 0.85) / 0.15;
    c.style.opacity = String(clamp01(op));
    if (r >= 0.999) this.complete();
  };

  Instance.prototype.drawField = function () {
    if (this.fieldDrawn && MOBILE) return;
    if (this.fieldDrawn && this.fieldClock < 0.18) return;
    this.fieldClock = 0;
    this.fieldDrawn = true;
    var w = Math.round(this.el.clientWidth * 0.5);
    var h = Math.round(this.el.clientHeight * 0.5);
    if (w < 4 || h < 4) return;
    var c = this.canvas;
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    var ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = this.fieldInk;
    var cell = 3;
    for (var y = 0; y < h; y += cell) {
      for (var x = 0; x < w; x += cell) {
        if (Math.random() < 0.42) { ctx.fillRect(x, y, cell, cell); }
      }
    }
  };

  Instance.prototype.complete = function () {
    this.done = true;
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    if (this.mode === 'scroll') {
      try { sessionStorage.setItem('pt-resolved', '1'); } catch (e) {}
    }
  };

  Instance.prototype.destroy = function () {
    this.done = true;
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  };

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var ins = entries[i].target.__ptResolve;
      if (ins) { ins.visible = entries[i].isIntersecting; }
    }
    ensureLoop();
  }, { rootMargin: '12% 0px' });

  function tick(t) {
    rafId = null;
    var dt = lastT ? Math.min(0.1, (t - lastT) / 1000) : 0.016;
    lastT = t;
    var sd = scrollDelta;
    scrollDelta = 0;
    var vh = window.innerHeight || 800;
    var active = false;
    for (var i = 0; i < instances.length; i++) {
      var ins = instances[i];
      if (ins.done || !ins.visible || !ins.ready) continue;
      if (ins.mode === 'never' && MOBILE && ins.fieldDrawn) continue;
      active = true;
      if (ins.mode === 'scroll') {
        ins.r = clamp01(
          ins.r +
          (sd / vh) * 0.55 +
          dt * 0.012 +
          (ins.r > 0.9 ? dt * 0.5 : 0) +
          (seen ? dt * 0.3 : 0)
        );
      } else if (ins.mode === 'fast') {
        ins.r = clamp01(ins.r + dt * 0.55 + (sd / vh) * 0.3);
      } else {
        ins.fieldClock += dt;
      }
      ins.draw();
    }
    if (active) { rafId = window.requestAnimationFrame(tick); }
    else { lastT = 0; }
  }

  function ensureLoop() {
    if (rafId !== null) return;
    for (var i = 0; i < instances.length; i++) {
      var ins = instances[i];
      if (!ins.done && ins.visible && ins.ready) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }
    }
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) { clearTimeout(resizeTimer); }
    resizeTimer = setTimeout(function () {
      for (var i = 0; i < instances.length; i++) {
        instances[i].lastDrawn = -1;
        instances[i].fieldDrawn = false;
      }
      ensureLoop();
    }, 200);
  });

  function init() {
    for (var i = 0; i < TARGETS.length; i++) {
      var tgt = TARGETS[i];
      if (MOBILE && !tgt.mobile) continue;
      var els = document.querySelectorAll(tgt.sel);
      for (var j = 0; j < els.length; j++) {
        var el = els[j];
        if (el.__ptResolve) continue;
        var ins = new Instance(el, tgt.mode);
        el.__ptResolve = ins;
        ins.mount();
        if (ins.done) continue;
        if (tgt.mode !== 'never') { ins.loadSource(); }
        instances.push(ins);
        io.observe(el);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
